import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { ReceiptLongOutlined, ChevronRightOutlined, VisibilityOutlined } from '@mui/icons-material';
import dayjs from 'dayjs';
import DashboardCard, {
  CardHeader,
  StatusPill,
  OpenLink,
  ColHeader,
  PrimaryButton,
  EmptyState
} from '../DashboardCard/DashboardCard';
import QuickLookDrawer from '../../QuickLookDrawer/QuickLookDrawer';

function formatExpenseDateRange(exp) {
  const created = exp.CreatedDate ?? exp.StartDate ?? exp.DateFrom;

  if (created) {
    const d = dayjs(created);
    return d.format('D MMM YYYY');
  }

  const details = exp.ExpenseDetails;
  if (details?.length) {
    const sorted = [...details].sort((a, b) => new Date(a.Date) - new Date(b.Date));
    const first = dayjs(sorted[0].Date);
    const last = dayjs(sorted.at(-1).Date);
    if (first.isSame(last, 'month')) return first.format('MMMM YYYY');
    return `${first.format('MMM')} – ${last.format('MMM YYYY')}`;
  }

  return '';
}

const CATEGORY_MAP = {
  1: 'Travel',
  2: 'Accommodation',
  3: 'Meals',
  4: 'Entertainment',
  5: 'Mileage',
  6: 'Other'
};

function getCategorySummary(exp) {
  const details = exp.ExpenseDetails;
  if (!details?.length) return null;

  const counts = {};
  details.forEach((d) => {
    const name = d.Category?.Name || CATEGORY_MAP[d.CategoryId] || `Type ${d.CategoryId}`;
    counts[name] = (counts[name] ?? 0) + 1;
  });

  const [[topName, topCount]] = Object.entries(counts).sort((a, b) => b[1] - a[1]);
  const others = details.length - topCount;
  if (others > 0) return `${topName} +${others} more`;
  return topName;
}

function formatAmount(exp) {
  const total = exp.TotalExpense ?? exp.TotalAmount ?? 0;
  const symbol = exp.ExpenseCurrency?.Symbol ?? exp.ExpenseDetails?.[0]?.ExpenseCurrency?.Symbol ?? '£';
  const currency = exp.ExpenseCurrency?.CurrencyName ?? exp.ExpenseDetails?.[0]?.ExpenseCurrency?.CurrencyName ?? '';

  const formatted = parseFloat(total).toLocaleString('en-GB', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

  return `${symbol}${formatted}${currency ? ` ${currency}` : ''}`;
}

function getContractorName(exp) {
  if (exp.User?.FirstName || exp.User?.LastName) {
    return `${exp.User.FirstName ?? ''} ${exp.User.LastName ?? ''}`.trim();
  }
  return exp.ClientName ?? '—';
}

function initials(name) {
  const parts = (name ?? '').trim().split(/\s+/);
  return `${parts[0]?.[0] ?? ''}${parts[1]?.[0] ?? ''}`.toUpperCase() || '—';
}

const AVATAR_COLORS = ['#0094B3', '#7C3AED', '#059669', '#DB2777', '#D97706', '#2563EB'];
function avatarColor(seed) {
  const str = String(seed ?? '');
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) hash = (hash * 31 + str.charCodeAt(i)) % AVATAR_COLORS.length;
  return AVATAR_COLORS[hash];
}

function FilterTag({ label, count, colorCls, dotCls, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 text-[13px] font-semibold ${colorCls} bg-transparent border-none cursor-pointer p-0 hover:opacity-70 transition-opacity`}
    >
      <span className={`w-2 h-2 rounded-full ${dotCls}`} />
      {count} {label}
    </button>
  );
}

function SkeletonRow() {
  return (
    <div className="flex items-center px-5 py-4 border-b border-[#E2E8ED] gap-3 animate-pulse">
      <div style={{ flex: 2.2 }}>
        <div className="h-3.5 bg-[#E2E8ED] rounded w-40 mb-1.5" />
        <div className="h-3 bg-[#E2E8ED] rounded w-28" />
      </div>
      <div className="h-3.5 bg-[#E2E8ED] rounded w-20 hidden md:block" style={{ flex: 1 }} />
      <div className="h-5 bg-[#E2E8ED] rounded-full w-20" style={{ flex: 1 }} />
      <div className="h-3.5 bg-[#E2E8ED] rounded w-10 hidden md:block" style={{ flex: 0.6 }} />
    </div>
  );
}

export default function ExpensesCard({ animDelay = 0, onApprove, onReject }) {
  const [quickLookItem, setQuickLookItem] = useState(null);
  const navigate = useNavigate();

  const expensesList = useSelector((s) => s.expenses?.data);
  const expensesPending = useSelector((s) => s.expenses?.pending);
  const isApprover = useSelector((s) => s.accessToParts?.tAndEApprover);
  const expenseValidation = useSelector((s) => s?.timesheet?.validate?.expense);
  const expensePlacementStatus = useSelector((s) => s?.timesheet?.validate?.timesheet?.IsValidPlacementStatus);
  const approverCounts = useSelector((s) => s.timesheet?.tandecounts);
  const contractorCounts = useSelector((s) => s.timesheet?.contractortandecounts?.Expense);

  const approvedCount = contractorCounts?.ApprovedCount ?? 0;
  const rejectedCount = contractorCounts?.RejectedCount ?? 0;
  const pendingCount = isApprover ? approverCounts?.PendingCount ?? 0 : contractorCounts?.SubmittedCount ?? 0;

  const isLoading = expensesPending || expensesList === null;
  const preview = (expensesList ?? []).slice(0, 3);

  const cols = isApprover
    ? [
        { label: 'Description', flex: 2.2 },
        { label: 'Contractor', flex: 1.2 },
        { label: 'Status', flex: 1 },
        { label: '', flex: 0.6 }
      ]
    : [
        { label: 'Description', flex: 2.2 },
        { label: 'Amount', flex: 1 },
        { label: 'Status', flex: 1 },
        { label: '', flex: 0.6 }
      ];

  return (
    <DashboardCard noPadding animDelay={animDelay}>
      <CardHeader
        icon={<ReceiptLongOutlined />}
        title="Expenses"
        action={
          <div className="flex items-center gap-3">
            {pendingCount > 0 && (
              <button
                onClick={() => navigate('/dashboard/expenses?status=Submitted')}
                className="flex items-center gap-1.5 text-[12px] font-semibold text-[#D97706] bg-[#FEF3C7] px-2.5 py-1 rounded-full border-none cursor-pointer hover:bg-[#FDE68A] transition-colors"
              >
                <span className="w-[5px] h-[5px] rounded-full bg-[#F59E0B] shrink-0" />
                {pendingCount} <span className="md:flex hidden">pending</span>
              </button>
            )}
            <span
              role="button"
              tabIndex={0}
              onClick={() => navigate('/dashboard/expenses')}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') navigate('/dashboard/expenses');
              }}
              className="text-[14px] text-[#0094B3] font-medium cursor-pointer hover:text-[#0081A0] transition-colors"
            >
              View all →
            </span>
          </div>
        }
      />

      {isApprover && pendingCount > 0 && (
        <p className="flex items-center gap-1.5 md:hidden text-[12px] text-[#9AA3AE] px-5 pb-3 -mt-2 m-0">
          <span aria-hidden>👆</span> Tap a row to review
        </p>
      )}

      {!isApprover && (
        <div className="hidden md:flex items-center gap-5 px-5 pb-4">
          <FilterTag
            label="Approved"
            count={approvedCount}
            colorCls="text-[#059669]"
            dotCls="bg-[#10B981]"
            onClick={() => navigate('/dashboard/expenses?status=Approved')}
          />
          <FilterTag
            label="Rejected"
            count={rejectedCount}
            colorCls="text-[#DC2626]"
            dotCls="bg-[#EF4444]"
            onClick={() => navigate('/dashboard/expenses?status=Rejected')}
          />
          <FilterTag
            label="Pending"
            count={pendingCount}
            colorCls="text-[#D97706]"
            dotCls="bg-[#F59E0B]"
            onClick={() => navigate('/dashboard/expenses?status=Submitted')}
          />
        </div>
      )}

      <div className="hidden md:block">
        <ColHeader cols={cols} />
      </div>

      {isLoading && [0, 1, 2].map((i) => <SkeletonRow key={i} />)}
      {!isLoading && preview.length === 0 && <EmptyState message="No expenses yet" />}
      {!isLoading &&
        preview.length !== 0 &&
        preview.map((exp) => {
          const claimName = exp.ClaimDescription ?? exp.ClaimName ?? '—';
          const dateRange = formatExpenseDateRange(exp);
          const categorySummary = getCategorySummary(exp);
          const lineCount = exp.ExpenseDetails?.length ?? exp.LineItemCount ?? 0;
          const contractorName = getContractorName(exp);

          return (
            <div key={exp.Id}>
              {/* Mobile row */}
              <div
                className="flex md:hidden items-center gap-3 px-5 py-3.5 border-b border-[#E2E8ED] last:border-b-0 active:bg-[#F8FAFB] transition-colors cursor-pointer"
                role="button"
                tabIndex={0}
                onClick={() => navigate(`/dashboard/expense/${exp.Id}`)}
                onKeyDown={(e) => {
                  if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
                    navigate(`/dashboard/expense/${exp.Id}`);
                  }
                }}
              >
                {isApprover ? (
                  <span
                    className="flex items-center justify-center w-9 h-9 rounded-full text-white text-[12px] font-bold shrink-0"
                    style={{ backgroundColor: avatarColor(exp.Id ?? contractorName) }}
                  >
                    {initials(contractorName)}
                  </span>
                ) : null}
                <div className="flex-1 min-w-0">
                  <div className="text-[15px] font-semibold text-[#022D41] leading-tight truncate">
                    {isApprover ? contractorName : claimName}
                  </div>
                  <div className="text-[12.5px] text-[#9AA3AE] mt-0.5 truncate">
                    {isApprover
                      ? `${claimName}${lineCount ? ` · ${lineCount} item${lineCount !== 1 ? 's' : ''}` : ''}`
                      : `${dateRange}${categorySummary ? ` · ${categorySummary}` : ''}`}
                  </div>
                </div>
                {!isApprover && (
                  <div className="text-[14px] font-semibold text-[#022D41] shrink-0">{formatAmount(exp)}</div>
                )}
                <StatusPill status={exp.Status} />
                {isApprover && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setQuickLookItem(exp);
                    }}
                    aria-label="Quick look"
                    className="flex items-center justify-center w-7 h-7 rounded-full bg-[#F0F4F7] border-none cursor-pointer shrink-0 hover:bg-[#E2E8ED] transition-colors"
                  >
                    <VisibilityOutlined sx={{ fontSize: 15, color: '#0094B3' }} />
                  </button>
                )}
                <ChevronRightOutlined sx={{ fontSize: 18, color: '#C4CDD5', flexShrink: 0 }} />
              </div>

              {/* Desktop row */}
              <div
                className="hidden md:flex items-center px-5 py-3.5 border-b border-[#E2E8ED] last:border-b-0 hover:bg-[#F8FAFB] transition-colors cursor-pointer group"
                role="button"
                tabIndex={0}
                onClick={() => navigate(`/dashboard/expense/${exp.Id}`)}
                onKeyDown={(e) => {
                  if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
                    navigate(`/dashboard/expense/${exp.Id}`);
                  }
                }}
              >
                <div style={{ flex: 2.2 }}>
                  <div className="text-[15px] font-semibold text-[#022D41] truncate group-hover:text-[#0094B3] transition-colors leading-tight">
                    {claimName}
                  </div>
                  <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                    {dateRange && <span className="text-[12px] text-[#9AA3AE]">{dateRange}</span>}
                    {categorySummary && (
                      <>
                        <span className="text-[#C4CDD5] text-[10px]">·</span>
                        <span className="text-[12px] text-[#9AA3AE]">{categorySummary}</span>
                      </>
                    )}
                    {!categorySummary && lineCount > 0 && (
                      <>
                        <span className="text-[#C4CDD5] text-[10px]">·</span>
                        <span className="text-[12px] text-[#9AA3AE]">
                          {lineCount} item{lineCount !== 1 ? 's' : ''}
                        </span>
                      </>
                    )}
                  </div>
                </div>

                {isApprover ? (
                  <div className="text-[14px] text-[#6B7A85] truncate" style={{ flex: 1.2 }}>
                    {contractorName}
                  </div>
                ) : (
                  <div className="text-[14px] font-semibold text-[#022D41] leading-tight" style={{ flex: 1 }}>
                    {formatAmount(exp)}
                  </div>
                )}

                <div style={{ flex: 1 }}>
                  <StatusPill status={exp.Status} />
                </div>

                <div style={{ flex: 0.6 }}>
                  <OpenLink onClick={() => navigate(`/dashboard/expense/${exp.Id}`)} />
                </div>
              </div>
            </div>
          );
        })}

      {!isApprover && (
        <div className="mt-auto">
          <PrimaryButton
            onClick={() => navigate('/dashboard/create-claim')}
            variant={expenseValidation?.IsValidAdminSettings && expensePlacementStatus ? 'dark' : 'disabled'}
          >
            + Add Expense
          </PrimaryButton>
        </div>
      )}

      {isApprover && pendingCount > 0 && (
        <div className="mt-auto">
          <PrimaryButton onClick={() => navigate('/dashboard/expenses?status=Pending')} variant="teal">
            Review {pendingCount} Pending Claim{pendingCount !== 1 ? 's' : ''}
          </PrimaryButton>
        </div>
      )}

      <QuickLookDrawer
        open={!!quickLookItem}
        item={quickLookItem}
        type="expense"
        onClose={() => setQuickLookItem(null)}
        onApprove={onApprove}
        onReject={onReject}
      />
    </DashboardCard>
  );
}
